import { z } from 'zod';

/**
 * Environment variable schema with validation
 */
const envSchema = z.object({
  VITE_API_BASE_URL: z.string().url('API base URL must be a valid URL'),
  VITE_WS_URL: z.string().url('WebSocket URL must be a valid URL').optional(),
  VITE_ENV: z.enum(['local', 'production']),
});

const removeTrailingSlashes = (value: string): string => value.replace(/\/+$/, '');

const normalizeWebSocketURL = (wsURL: string | undefined, apiBaseURL: string): string => {
  const apiURL = new URL(apiBaseURL);
  const urlValue = wsURL?.trim() || `${removeTrailingSlashes(apiBaseURL)}/cable`;
  const parsedURL = new URL(urlValue);

  if (parsedURL.protocol === 'http:') {
    parsedURL.protocol = 'ws:';
  } else if (parsedURL.protocol === 'https:') {
    parsedURL.protocol = 'wss:';
  }

  if (parsedURL.protocol !== 'ws:' && parsedURL.protocol !== 'wss:') {
    throw new Error(`WebSocket URL must use ws:// or wss://, received ${parsedURL.protocol}`);
  }

  // Keep websocket security level aligned with API transport defaults.
  if (apiURL.protocol === 'https:' && parsedURL.protocol === 'ws:') {
    parsedURL.protocol = 'wss:';
  } else if (apiURL.protocol === 'http:' && parsedURL.protocol === 'wss:') {
    parsedURL.protocol = 'ws:';
  }

  // ActionCable endpoint defaults to /cable when only a host is provided.
  if (parsedURL.pathname === '/' || parsedURL.pathname === '') {
    parsedURL.pathname = '/cable';
  }

  return parsedURL.toString();
};

/**
 * Parse and validate environment variables
 */
const parseEnv = () => {
  const env = {
    VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
    VITE_WS_URL: import.meta.env.VITE_WS_URL,
    VITE_ENV: import.meta.env.VITE_ENV,
  };

  try {
    const parsedEnv = envSchema.parse(env);
    const apiBaseURL = removeTrailingSlashes(parsedEnv.VITE_API_BASE_URL);

    return {
      ...parsedEnv,
      VITE_API_BASE_URL: apiBaseURL,
      VITE_WS_URL: normalizeWebSocketURL(parsedEnv.VITE_WS_URL, apiBaseURL),
    };
  } catch (error) {
    console.error('❌ Invalid environment variables:', error);
    throw new Error('Invalid environment configuration. Check your .env file.');
  }
};

const env = parseEnv();

/**
 * Type-safe configuration object
 */
export const config = {
  api: {
    baseURL: env.VITE_API_BASE_URL,
    timeout: 10000,
  },
  websocket: {
    url: env.VITE_WS_URL,
    reconnectInterval: 3000,
    maxReconnectAttempts: 10,
  },
  env: env.VITE_ENV,
  isDevelopment: env.VITE_ENV === 'local',
  isProduction: env.VITE_ENV === 'production',
} as const;

/**
 * Log configuration on startup (only in development)
 */
if (config.isDevelopment) {
  console.log('🚀 App Configuration:', {
    environment: config.env,
    apiBaseURL: config.api.baseURL,
    wsURL: config.websocket.url,
  });
}
